/**
 * Phone number normalization — US-only for MVP.
 *
 * Mirrors backend/src/lib/phone.ts intentionally (no shared package yet).
 * If you change one, update the other.
 *
 * Accepts a wide range of human-typed formats and produces a clean E.164.
 * Anything that doesn't reduce to a 10- or 11-digit (with leading 1) US
 * number returns ok:false with a reason.
 */

export type PhoneParseResult =
  | { ok: true; e164: string; nationalDigits: string; formatted: string }
  | { ok: false; reason: string };

function stripFormatting(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  const hasPlus = trimmed.startsWith("+");
  const digitsOnly = trimmed.replace(/\D/g, "");
  return hasPlus ? `+${digitsOnly}` : digitsOnly;
}

export function parseUSPhone(input: string | null | undefined): PhoneParseResult {
  if (!input) return { ok: false, reason: "empty" };
  const stripped = stripFormatting(input);
  if (!stripped) return { ok: false, reason: "empty" };

  const digits = stripped.replace(/\D/g, "");
  if (digits.length === 0) return { ok: false, reason: "no digits" };

  if (stripped.startsWith("+") && !stripped.startsWith("+1")) {
    return { ok: false, reason: "non-US country code (US-only during MVP)" };
  }

  let nationalDigits: string;
  if (digits.length === 11 && digits.startsWith("1")) {
    nationalDigits = digits.slice(1);
  } else if (digits.length === 10) {
    nationalDigits = digits;
  } else {
    return { ok: false, reason: `expected 10 digits (or 11 with leading 1); got ${digits.length}` };
  }

  if (nationalDigits.startsWith("0") || nationalDigits.startsWith("1")) {
    return { ok: false, reason: "invalid US area code (starts with 0 or 1)" };
  }
  const exchange = nationalDigits.slice(3, 6);
  if (exchange.startsWith("0") || exchange.startsWith("1")) {
    return { ok: false, reason: "invalid US exchange code (starts with 0 or 1)" };
  }

  const e164 = `+1${nationalDigits}`;
  const formatted = `(${nationalDigits.slice(0, 3)}) ${nationalDigits.slice(3, 6)}-${nationalDigits.slice(6)}`;
  return { ok: true, e164, nationalDigits, formatted };
}

/**
 * Live-format a partial input as the user types. Tolerant — returns whatever
 * makes sense for an in-progress phone number. Strips +1 for display, then
 * formats as `(AAA) XXX-YYYY`.
 */
export function formatPhoneInput(input: string): string {
  const digits = input.replace(/\D/g, "");
  // If user typed +1... or 1..., drop the leading 1 for the display formatter.
  const local = digits.startsWith("1") && digits.length > 10 ? digits.slice(1) : digits;
  const a = local.slice(0, 3);
  const b = local.slice(3, 6);
  const c = local.slice(6, 10);
  if (local.length === 0) return "";
  if (local.length <= 3) return `(${a}`;
  if (local.length <= 6) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

/**
 * Pretty-print an E.164 (or near-E.164) for read-only display.
 * Falls back to the raw input on anything we don't recognize so a non-NANP
 * number still renders rather than 404'ing the UI.
 */
export function formatE164ForDisplay(raw: string | null | undefined): string {
  if (!raw) return "unknown";
  const parsed = parseUSPhone(raw);
  if (parsed.ok) return parsed.formatted;
  return raw;
}
